import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Plotly from "plotly.js-dist-min";
import { CoordinateTransformer } from "../../site-layout/coordinateTransformer";
import type { CalibrationPoint } from "../../site-layout/types";

export interface ContourPoint {
  x: number; // image pixel x (or fallback coord)
  y: number; // image pixel y
  z: number; // value
  name: string;
  easting?: number;
  northing?: number;
}

export interface ContourPlotProps {
  points: ContourPoint[];
  backgroundUrl?: string | null;
  /** Calibration points from the site layout — when provided, used instead of
   *  estimating the affine transform from device positions (more accurate). */
  calibrationPoints?: CalibrationPoint[];
  plotTitle?: string;
  zAxisTitle?: string;
  numberFormat?: (v: number) => string;
  gridSize?: number;
  opacity?: number;
}

// Persisted outside the component so controls survive date-change remounts.
let _savedColorEnabled = true;
let _savedContourOpacity: number | null = null; // null → use opacity prop on first mount
let _savedImageOpacity = 1;

const DEFAULT_COLOR_SCALE: Array<[number, string]> = [
  [0, "rgba(0,0,255,0.35)"],
  [0.25, "rgba(0,255,255,0.35)"],
  [0.5, "rgba(0,255,0,0.35)"],
  [0.75, "rgba(255,255,0,0.35)"],
  [1, "rgba(255,0,0,0.35)"],
];

// Inverse-distance-weighted interpolation (power 2).
function interpolatePoint(
  points: ContourPoint[],
  px: number,
  py: number,
  power = 2,
): number {
  let weightedSum = 0;
  let weightSum = 0;
  for (const point of points) {
    const d = Math.sqrt((px - point.x) ** 2 + (py - point.y) ** 2);
    if (d < 0.001) return point.z;
    const w = 1 / d ** power;
    weightedSum += w * point.z;
    weightSum += w;
  }
  return weightedSum / weightSum;
}

function createInterpolatedGrid(
  points: ContourPoint[],
  bounds: { xMin: number; xMax: number; yMin: number; yMax: number },
  gridSize: number,
) {
  const { xMin, xMax, yMin, yMax } = bounds;
  const xStep = (xMax - xMin) / gridSize;
  const yStep = (yMax - yMin) / gridSize;
  const gridX = Array.from(
    { length: gridSize + 1 },
    (_, i) => xMin + i * xStep,
  );
  const gridY = Array.from(
    { length: gridSize + 1 },
    (_, j) => yMin + j * yStep,
  );
  const gridZ: number[][] = [];
  for (let j = 0; j <= gridSize; j++) {
    const row: number[] = [];
    for (let i = 0; i <= gridSize; i++) {
      row.push(interpolatePoint(points, gridX[i], gridY[j]));
    }
    gridZ.push(row);
  }
  return { gridX, gridY, gridZ };
}

const ContourPlot: React.FC<ContourPlotProps> = ({
  points,
  backgroundUrl,
  calibrationPoints,
  plotTitle = "Contour Plot",
  zAxisTitle = "Value",
  numberFormat,
  gridSize = 30,
  opacity = 0.6,
}) => {
  const plotRef = useRef<HTMLDivElement>(null);
  const [imageDimensions, setImageDimensions] = useState<{
    width: number;
    height: number;
  } | null>(null);
  const [loadedImage, setLoadedImage] = useState<HTMLImageElement | null>(null);

  // ── UI controls (initialised from module-level vars so they survive remounts)
  const [colorEnabled, setColorEnabled] = useState(() => _savedColorEnabled);
  const [contourOpacity, setContourOpacity] = useState(
    () => _savedContourOpacity ?? opacity,
  );
  const [imageOpacity, setImageOpacity] = useState(() => _savedImageOpacity);

  // Pre-rendered canvas data URL: the site layout warped into real-world (E/N) space
  const [preRender, setPreRender] = useState<{
    url: string;
    eMin: number;
    eMax: number;
    nMin: number;
    nMax: number;
  } | null>(null);

  const fmt = useMemo(
    () => numberFormat ?? ((v: number) => v.toFixed(3)),
    [numberFormat],
  );

  // Load background image — store object so pre-render effect can draw it.
  useEffect(() => {
    if (!backgroundUrl) {
      setImageDimensions(null);
      setLoadedImage(null);
      return;
    }
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      setImageDimensions({
        width: img.naturalWidth,
        height: img.naturalHeight,
      });
      setLoadedImage(img);
    };
    img.onerror = () => {
      setImageDimensions(null);
      setLoadedImage(null);
    };
    img.src = backgroundUrl;
  }, [backgroundUrl]);

  // Warp the site layout image onto an axis-aligned offscreen canvas in real-world
  // (Easting × Northing) space. Plotly can then place this as a correctly-oriented,
  // undistorted background image — Plotly's image layer has no rotation support,
  // so we pre-apply the affine transform here.
  useEffect(() => {
    if (!loadedImage || !imageDimensions) {
      setPreRender(null);
      return;
    }

    // ── Build affine transform ─────────────────────────────────────────────
    // Prefer calibrationPoints (surveyed, same method as SiteLayoutManager) when
    // available; fall back to estimating from device positions.
    let a: number, b: number, tx: number, ty: number, det: number;

    if (calibrationPoints && calibrationPoints.length >= 2) {
      let transformer: CoordinateTransformer;
      try {
        transformer = new CoordinateTransformer(calibrationPoints);
      } catch {
        setPreRender(null);
        return;
      }
      ({ a, b, tx, ty } = transformer.getParams());
      det = a * a + b * b;
    } else {
      const hasRW =
        points.length >= 2 &&
        points.every(
          (p) =>
            p.easting !== undefined &&
            p.northing !== undefined &&
            Number.isFinite(p.easting) &&
            Number.isFinite(p.northing) &&
            (p.easting !== 0 || p.northing !== 0),
        );
      if (!hasRW) {
        setPreRender(null);
        return;
      }
      // Pick the most-separated pair for a well-conditioned estimate
      let pt1 = points[0],
        pt2 = points[1];
      let best = 0;
      for (let i = 0; i < points.length; i++) {
        for (let j = i + 1; j < points.length; j++) {
          const d = Math.hypot(
            points[j].x - points[i].x,
            points[j].y - points[i].y,
          );
          if (d > best) {
            best = d;
            pt1 = points[i];
            pt2 = points[j];
          }
        }
      }
      const de = pt2.easting! - pt1.easting!;
      const dn = pt2.northing! - pt1.northing!;
      const dix = pt2.x - pt1.x;
      const diy = pt2.y - pt1.y;
      const D = de * de + dn * dn;
      if (!D) {
        setPreRender(null);
        return;
      }
      a = (dix * de - diy * dn) / D;
      b = (dix * dn + diy * de) / D;
      tx = pt1.x - a * pt1.easting! - b * pt1.northing!;
      ty = pt1.y - b * pt1.easting! + a * pt1.northing!;
      det = a * a + b * b;
    }

    if (!det) {
      setPreRender(null);
      return;
    }

    const { width: imgW, height: imgH } = imageDimensions;
    const imgToRW = (ix: number, iy: number) => {
      const dx = ix - tx,
        dy = iy - ty;
      return { e: (a * dx + b * dy) / det, n: (b * dx - a * dy) / det };
    };
    const corners = [
      imgToRW(0, 0),
      imgToRW(imgW, 0),
      imgToRW(0, imgH),
      imgToRW(imgW, imgH),
    ];
    const eMin = Math.min(...corners.map((c) => c.e));
    const eMax = Math.max(...corners.map((c) => c.e));
    const nMin = Math.min(...corners.map((c) => c.n));
    const nMax = Math.max(...corners.map((c) => c.n));
    const spanE = eMax - eMin || 1;
    const spanN = nMax - nMin || 1;

    // Canvas sized to the real-world bounding box, preserving its aspect ratio.
    // Using a single S keeps pixels square → no stretching.
    const MAX_PX = 1024;
    const S = Math.min(MAX_PX / spanE, MAX_PX / spanN);
    const canvasW = Math.max(1, Math.round(S * spanE));
    const canvasH = Math.max(1, Math.round(S * spanN));

    const offscreen = document.createElement("canvas");
    offscreen.width = canvasW;
    offscreen.height = canvasH;
    const ctx = offscreen.getContext("2d");
    if (!ctx) {
      setPreRender(null);
      return;
    }

    // Affine: image pixel → canvas pixel (same formula as SiteLayoutCanvas calibrated):
    //   screen_x = (e - eMin) * S
    //   screen_y = (nMax - n) * S
    ctx.setTransform(
      /* m11 */ (S * a) / det,
      /* m12 */ -(S * b) / det,
      /* m21 */ (S * b) / det,
      /* m22 */ (S * a) / det,
      /* dx  */ S * (-(a * tx + b * ty) / det - eMin),
      /* dy  */ S * (nMax + (b * tx - a * ty) / det),
    );
    ctx.drawImage(loadedImage, 0, 0);

    setPreRender({
      url: offscreen.toDataURL("image/png"),
      eMin,
      eMax,
      nMin,
      nMax,
    });
  }, [loadedImage, imageDimensions, calibrationPoints, points]);

  const createPlot = useCallback(() => {
    const el = plotRef.current;
    if (!el) return;

    if (points.length < 3) {
      el.innerHTML = `
        <div style="padding:40px;text-align:center;color:#64748b">
          <h3 style="margin:0 0 8px">Contour plot needs at least 3 checkpoints</h3>
          <p style="margin:0">Currently ${points.length} checkpoint${
            points.length === 1 ? "" : "s"
          } with data on this date.</p>
        </div>`;
      return;
    }

    const hasRealCoords = points.every(
      (p) =>
        p.easting !== undefined &&
        p.northing !== undefined &&
        Number.isFinite(p.easting) &&
        Number.isFinite(p.northing) &&
        (p.easting !== 0 || p.northing !== 0),
    );

    const x = hasRealCoords
      ? points.map((p) => p.easting!)
      : points.map((p) => p.x);
    const y = hasRealCoords
      ? points.map((p) => p.northing!)
      : points.map((p) => p.y);
    const z = points.map((p) => p.z);
    const names = points.map((p) => p.name);

    const xPadding = (Math.max(...x) - Math.min(...x)) * 0.1 || 10;
    const yPadding = (Math.max(...y) - Math.min(...y)) * 0.1 || 10;
    const bounds = hasRealCoords
      ? {
          xMin: Math.min(...x) - xPadding,
          xMax: Math.max(...x) + xPadding,
          yMin: Math.min(...y) - yPadding,
          yMax: Math.max(...y) + yPadding,
        }
      : {
          xMin: 0,
          xMax: imageDimensions?.width || 800,
          yMin: 0,
          yMax: imageDimensions?.height || 600,
        };

    const interpPoints: ContourPoint[] = points.map((p, i) => ({
      ...p,
      x: x[i],
      y: y[i],
    }));

    const { gridX, gridY, gridZ } = createInterpolatedGrid(
      interpPoints,
      bounds,
      gridSize,
    );

    const traces: any[] = [
      {
        showscale: colorEnabled,
        x: gridX,
        y: gridY,
        z: gridZ,
        type: "contour",
        colorscale: colorEnabled
          ? DEFAULT_COLOR_SCALE
          : ([
              [0, "rgb(60,60,60)"],
              [1, "rgb(60,60,60)"],
            ] as Array<[number, string]>),
        opacity: contourOpacity,
        colorbar: colorEnabled
          ? { title: zAxisTitle, titleside: "right" }
          : undefined,
        contours: {
          coloring: colorEnabled ? "fill" : "lines",
          showlabels: true,
          labelfont: { size: 14, color: "#b91c1c" },
        },
        line: { smoothing: 1.3, width: colorEnabled ? 1 : 2 },
        hoverinfo: "skip",
        name: "Contour",
      },
      {
        x,
        y,
        type: "scatter",
        mode: "markers",
        marker: {
          size: 10,
          color: "#16a34a",
          symbol: "circle",
          line: { width: 2, color: "#000" },
        },
        hovertemplate: names.map(
          (name, i) =>
            `<b>${name}</b><br>${zAxisTitle}: ${fmt(z[i])}<extra></extra>`,
        ),
        showlegend: false,
      },
    ];

    const annotations = points.map((p, i) => ({
      x: x[i],
      y: y[i],
      xref: "x" as const,
      yref: "y" as const,
      text: `<b>${p.name}</b><br>${fmt(p.z)}`,
      showarrow: true,
      arrowhead: 2,
      arrowsize: 1,
      arrowwidth: 1.5,
      arrowcolor: "#333",
      ax: 30,
      ay: -30,
      font: { size: 11, color: "#333", family: "Arial, sans-serif" },
      bgcolor: "rgba(255,255,255,0.85)",
      bordercolor: "#999",
      borderwidth: 1,
      borderpad: 4,
    }));

    const imgW = imageDimensions?.width ?? 800;
    const imgH = imageDimensions?.height ?? 600;
    const width = el.clientWidth || 900;
    const height = (width / imgW) * imgH;

    const layout: any = {
      title: { text: plotTitle, font: { size: 15 } },
      xaxis: {
        title: hasRealCoords ? "Easting" : "X",
        scaleanchor: "y",
        exponentformat: "none",
        showticklabels: hasRealCoords,
      },
      yaxis: {
        title: hasRealCoords ? "Northing" : "Y",
        exponentformat: "none",
        showticklabels: hasRealCoords,
        ...(hasRealCoords ? {} : { autorange: "reversed" }),
      },
      width,
      height,
      hovermode: "closest",
      showlegend: false,
      dragmode: "pan",
      margin: { l: 60, r: 30, t: 50, b: 50 },
      annotations,
    };

    if (backgroundUrl) {
      if (preRender) {
        // Use the pre-warped canvas that already has the correct affine orientation.
        // sizex/sizey are in Easting/Northing units; Plotly stretches to exactly that
        // rectangle — which matches the canvas aspect ratio, so no distortion occurs.
        layout.images = [
          {
            source: preRender.url,
            xref: "x",
            yref: "y",
            x: preRender.eMin,
            y: preRender.nMax,
            sizex: preRender.eMax - preRender.eMin,
            sizey: preRender.nMax - preRender.nMin,
            xanchor: "left",
            yanchor: "top",
            sizing: "stretch",
            opacity: imageOpacity,
            layer: "below",
          },
        ];
      } else {
        // Fallback: uncalibrated pixel-space image
        layout.images = [
          {
            source: backgroundUrl,
            xref: "x",
            yref: "y",
            x: 0,
            y: 0,
            sizex: imgW,
            sizey: imgH,
            sizing: "fill",
            opacity: imageOpacity,
            layer: "below",
          },
        ];
      }
    }

    const config = {
      responsive: true,
      displayModeBar: true,
      scrollZoom: true,
      modeBarButtonsToRemove: ["lasso2d", "select2d"],
      displaylogo: false,
    };

    Plotly.purge(el);
    Plotly.newPlot(el, traces, layout, config);
  }, [
    points,
    backgroundUrl,
    plotTitle,
    zAxisTitle,
    fmt,
    gridSize,
    imageDimensions,
    preRender,
    colorEnabled,
    contourOpacity,
    imageOpacity,
  ]);

  useEffect(() => {
    createPlot();
    const el = plotRef.current;
    return () => {
      if (el) Plotly.purge(el);
    };
  }, [createPlot]);

  return (
    <div style={{ width: "75%" }}>
      {/* ── Controls toolbar ─────────────────────────────────────────── */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "1.5rem",
          padding: "6px 10px",
          background: "#f8fafc",
          border: "1px solid #e2e8f0",
          borderRadius: "6px",
          marginBottom: "6px",
          flexWrap: "wrap",
          fontSize: "13px",
          color: "#374151",
        }}
      >
        {/* 1. Color toggle */}
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: "5px",
            cursor: "pointer",
          }}
        >
          <input
            type="checkbox"
            checked={colorEnabled}
            onChange={(e) => {
              _savedColorEnabled = e.target.checked;
              setColorEnabled(e.target.checked);
            }}
          />
          Contour Color fill
        </label>

        {/* 2. Contour opacity */}
        <label style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          Contour opacity
          <input
            type="range"
            min={0}
            max={1}
            step={0.05}
            value={contourOpacity}
            onChange={(e) => {
              _savedContourOpacity = Number(e.target.value);
              setContourOpacity(Number(e.target.value));
            }}
            style={{ width: "90px" }}
          />
          <span style={{ minWidth: "30px" }}>
            {Math.round(contourOpacity * 100)}%
          </span>
        </label>

        {/* 3. Image opacity */}
        {backgroundUrl && (
          <label style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            Image opacity
            <input
              type="range"
              min={0}
              max={1}
              step={0.05}
              value={imageOpacity}
              onChange={(e) => {
                _savedImageOpacity = Number(e.target.value);
                setImageOpacity(Number(e.target.value));
              }}
              style={{ width: "90px" }}
            />
            <span style={{ minWidth: "30px" }}>
              {Math.round(imageOpacity * 100)}%
            </span>
          </label>
        )}
      </div>

      <div ref={plotRef} />
    </div>
  );
};

export default ContourPlot;
